import React, { ReactNode, useId } from "react";
import { Trans } from "react-i18next";

import {
  useDashboardTiles,
  EngagementTiles,
} from "apps/company/api/rest/company-dashboard-tiles";
import { useTranslation } from "lib/i18n/use-translation";

import Info from "../Info";

import {
  engagementView,
  updatedAgo,
  EngagementView,
  FunnelStep,
} from "./engagement-view";
import styles from "./engagement.module.scss";

const K = "admin:companyDashboard.engagement";

// Each topic takes the accent at a lighter strength, so the chart stays in
// the one brand colour; topics past the last strength reuse it.
const SEGMENT_STRENGTHS = [1, 0.72, 0.48, 0.3, 0.18];
const strength = (index: number) =>
  SEGMENT_STRENGTHS[Math.min(index, SEGMENT_STRENGTHS.length - 1)];

export const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => {
  const headingId = useId();
  return (
    <section className={styles["card"]} aria-labelledby={headingId}>
      <h4 id={headingId} className={styles["card-title"]}>
        {title}
      </h4>
      {description && (
        <p className={styles["card-description"]}>{description}</p>
      )}
      {children}
    </section>
  );
};

export const NotEnoughData = () => {
  const { t } = useTranslation(["admin"]);
  return <p className={styles["not-enough"]}>{t(`${K}.notEnoughData`)}</p>;
};

export const StatCard = ({
  label,
  value,
  detail,
  info,
}: {
  label: string;
  value: string | null;
  detail?: string | null;
  info: string;
}) => {
  const labelId = useId();
  const heading = (
    <h4 id={labelId} className={styles["stat-label"]}>
      {label}
    </h4>
  );
  return (
    <section className={styles["stat"]} aria-labelledby={labelId}>
      <span className={styles["stat-info"]}>
        <Info figure={label} text={info} />
      </span>
      {value === null ? (
        <>
          {heading}
          <NotEnoughData />
        </>
      ) : (
        <>
          <p className={styles["stat-value"]}>{value}</p>
          {heading}
          {detail && <p className={styles["stat-detail"]}>{detail}</p>}
        </>
      )}
    </section>
  );
};

const Bar = ({ width, strength = 1 }: { width: number; strength?: number }) => (
  <span className={styles["bar-track"]} aria-hidden="true">
    <span
      className={styles["bar-fill"]}
      style={{ width: `${width * 100}%`, opacity: strength }}
    />
  </span>
);

const Funnel = ({ funnel }: { funnel: EngagementView["funnel"] }) => {
  const { t } = useTranslation(["admin"]);
  const steps: { key: string; step: FunnelStep; shareOf: string }[] = [
    { key: "activated", step: funnel.activated, shareOf: "ofWorkforce" },
    { key: "everEngaged", step: funnel.everEngaged, shareOf: "ofActivated" },
    {
      key: "repeatEngaged",
      step: funnel.repeatEngaged,
      shareOf: "ofEverEngaged",
    },
  ];
  return (
    <Card
      title={t(`${K}.funnel.title`)}
      description={t(`${K}.funnel.description`)}
    >
      <ol className={styles["funnel"]}>
        {steps.map(({ key, step, shareOf }, index) => (
          <li key={key} className={styles["funnel-step"]}>
            <span className={styles["funnel-head"]}>
              <span className={styles["funnel-name"]}>
                {t(`${K}.funnel.${key}`)}
                <Info
                  figure={t(`${K}.funnel.${key}`)}
                  text={t(`${K}.funnel.${key}Info`)}
                  align="start"
                />
              </span>
              {step.value !== null && (
                <span className={styles["figure"]}>
                  <strong>{step.value}</strong>
                  {step.share && (
                    <span className={styles["figure-note"]}>
                      {t(`${K}.funnel.${shareOf}`, { share: step.share })}
                    </span>
                  )}
                </span>
              )}
            </span>
            {step.value === null ? (
              <NotEnoughData />
            ) : (
              <Bar width={step.width} strength={1 - index * 0.28} />
            )}
          </li>
        ))}
      </ol>
    </Card>
  );
};

// A circle of this radius has a circumference of 100, so each segment's dash
// length is simply its percentage.
const DONUT_RADIUS = 15.915;

const Topics = ({ topics }: { topics: EngagementView["topics"] }) => {
  const { t } = useTranslation(["admin"]);
  const name = (topic: string) =>
    t(`${K}.topics.names.${topic}`, { defaultValue: topic });

  // Says what the split is drawn from whenever part of the engagement has no
  // topic — including when none of it does, where "none" alone would read as
  // "nothing was completed".
  const coverage = topics && !topics.complete && (
    <p className={styles["footnote"]}>
      {t(`${K}.topics.coverage`, {
        counted: topics.counted,
        total: topics.total,
      })}
    </p>
  );

  let offset = 0;
  return (
    <Card
      title={t(`${K}.topics.title`)}
      description={t(`${K}.topics.description`)}
    >
      {topics === null ? (
        <NotEnoughData />
      ) : topics.rows.length === 0 ? (
        <>
          <p className={styles["not-enough"]}>{t(`${K}.topics.none`)}</p>
          {coverage}
        </>
      ) : (
        <>
          <svg
            className={styles["donut"]}
            viewBox="0 0 42 42"
            aria-hidden="true"
            focusable="false"
          >
            {topics.rows.map((row, index) => {
              const length = row.fraction * 100;
              // Starts at twelve o'clock and runs clockwise.
              const segment = (
                <circle
                  key={row.topic}
                  className={styles["donut-segment"]}
                  cx="21"
                  cy="21"
                  r={DONUT_RADIUS}
                  strokeOpacity={strength(index)}
                  strokeDasharray={`${length} ${100 - length}`}
                  strokeDashoffset={25 - offset}
                />
              );
              offset += length;
              return segment;
            })}
          </svg>
          <ul className={styles["legend"]}>
            {topics.rows.map((row, index) => (
              <li key={row.topic} className={styles["legend-item"]}>
                <span
                  className={styles["legend-dot"]}
                  style={{ opacity: strength(index) }}
                  aria-hidden="true"
                />
                <span className={styles["legend-text"]}>
                  <span className={styles["legend-name"]}>
                    {name(row.topic)}
                  </span>
                  <span className={styles["figure-note"]}>{row.share}</span>
                </span>
              </li>
            ))}
          </ul>
          {coverage}
        </>
      )}
    </Card>
  );
};

const Popular = ({ popular }: { popular: EngagementView["popular"] }) => {
  const { t } = useTranslation(["admin"]);
  return (
    <Card
      title={t(`${K}.popular.title`)}
      description={t(`${K}.popular.description`)}
    >
      {popular === null ? (
        <NotEnoughData />
      ) : popular.top === null ? (
        <p className={styles["not-enough"]}>{t(`${K}.popular.none`)}</p>
      ) : (
        <>
          <p className={styles["values-note"]}>
            {t(`${K}.popular.valuesNote`)}
          </p>
          <ol className={styles["ranks"]}>
            {popular.rows.map((row, index) => (
              <li key={`${index}-${row.title}`} className={styles["rank"]}>
                <span className={styles["rank-index"]}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className={styles["rank-body"]}>
                  <span className={styles["rank-title"]}>{row.title}</span>
                  <Bar width={row.width} />
                </span>
                <span className={styles["figure"]}>
                  <strong>{row.minutes}</strong>
                </span>
              </li>
            ))}
          </ol>
          <p className={styles["summary"]}>
            <Trans
              i18nKey={`${K}.popular.summary`}
              values={{
                title: popular.top.title,
                minutes: popular.top.minutes,
              }}
              components={{ strong: <strong /> }}
            />
          </p>
        </>
      )}
    </Card>
  );
};

const EngagementTab = () => {
  const { t, i18n } = useTranslation(["admin"]);
  const tiles = useDashboardTiles();

  const header = (
    <header className={styles["header"]}>
      <h2 className={styles["heading"]}>{t(`${K}.tab`)}</h2>
      {"companyName" in tiles && (
        <p className={styles["subheading"]}>
          {t(`${K}.subtitle`, { company: tiles.companyName })}
        </p>
      )}
      {tiles.state === "ready" && (
        <p className={styles["freshness"]}>
          {t(`${K}.updated`, {
            time: updatedAgo(tiles.computedAt, new Date(), i18n.language),
          })}
        </p>
      )}
    </header>
  );

  if (tiles.state !== "ready") {
    return (
      <div className={styles["engagement"]}>
        {header}
        {tiles.state === "loading" && (
          <div className={styles["status"]} aria-busy="true" />
        )}
        {tiles.state === "computing" && (
          <div className={styles["status"]} role="status">
            <h3 className={styles["card-title"]}>
              {t(`${K}.computing.title`)}
            </h3>
            <p className={styles["card-description"]}>
              {t(`${K}.computing.body`)}
            </p>
          </div>
        )}
        {tiles.state === "error" && (
          <div className={styles["status"]} role="alert">
            <p className={styles["card-description"]}>{t(`${K}.error`)}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles["engagement"]}>
      {header}
      <EngagementFigures tiles={tiles.tiles} />
    </div>
  );
};

export const EngagementFigures = ({ tiles }: { tiles: EngagementTiles }) => {
  const { t, i18n } = useTranslation(["admin"]);
  const view = engagementView(tiles, i18n.language);

  return (
    <>
      <h3 className={styles["section-label"]}>
        {t(`${K}.sections.activation`)}
      </h3>
      <div className={styles["stats"]}>
        <StatCard
          label={t(`${K}.stats.employeesInScope`)}
          value={view.employeesInScope.value}
          info={t(`${K}.stats.employeesInScopeInfo`)}
        />
        <StatCard
          label={t(`${K}.stats.activated`)}
          value={view.activated.value}
          detail={
            view.activated.share &&
            t(`${K}.stats.ofWorkforce`, { share: view.activated.share })
          }
          info={t(`${K}.stats.activatedInfo`)}
        />
        <StatCard
          label={t(`${K}.stats.everEngaged`)}
          value={view.everEngaged.value}
          detail={
            view.everEngaged.share &&
            t(`${K}.stats.ofActivated`, { share: view.everEngaged.share })
          }
          info={t(`${K}.stats.everEngagedInfo`)}
        />
        <StatCard
          label={t(`${K}.stats.minutes`)}
          value={view.minutes.value}
          detail={
            view.minutes.average &&
            t(`${K}.stats.minutesDetail`, { minutes: view.minutes.average })
          }
          info={t(`${K}.stats.minutesInfo`)}
        />
      </div>

      <h3 className={styles["section-label"]}>
        {t(`${K}.sections.engagement`)}
      </h3>
      <div className={styles["cards"]}>
        <Funnel funnel={view.funnel} />
        <Topics topics={view.topics} />
        <Popular popular={view.popular} />
      </div>
    </>
  );
};

export default EngagementTab;
